import { OrderForAssetAvailability } from '../../../api/types';
import { atStation, pickedDay, stationCalendarDay, stationInstant } from '../../../utils/stationTime';
import { occupiesAsset } from './availabilityStatus';

// Turning a day's bookings into a timeline: where each booking sits on the
// clock, and — the question the day view exists to answer — which stretches of
// the day are still free.
//
// Positions are minutes since the STATION's midnight (ADR-0019) — the
// operator's browser zone moved every booking by the offset between the two.
// The day is always 1440
// minutes long no matter what the clock did that night. A DST day really has
// 23 or 25 hours, but the operator reads a wall clock: a booking that starts at
// 09:00 belongs under the 09 column, and no arithmetic should move it.

export const MINUTES_PER_DAY = 1440;

/** A stretch of the day, in minutes since station midnight. `end` is exclusive. */
export interface DaySpan {
    startMinute: number;
    endMinute: number;
}

const minuteOfDay = (date: Date): number => {
    const atStationClock = atStation(date);
    return atStationClock.hour * 60 + atStationClock.minute;
};

const clampToDay = (minute: number): number =>
    Math.min(MINUTES_PER_DAY, Math.max(0, minute));

/**
 * Where a booking sits on `day`'s clock, clamped to that day.
 *
 * A booking that started earlier begins at 00:00; one that runs on ends at
 * 24:00. A booking ending exactly at midnight ends at 24:00 of the day before,
 * which matches how the month chart decides a booking still colours that day.
 */
export const orderSpanWithinDay = (
    order: Pick<OrderForAssetAvailability, 'startDate' | 'endDate'>,
    day: Date,
): DaySpan => {
    // `day` is the chart's browser-local midnight of the day shown; its label
    // is the station day.
    const dayKey = pickedDay(day);
    const dayStart = stationInstant(dayKey);
    const start = new Date(order.startDate);
    const end = new Date(order.endDate);

    let startMinute: number;
    if (stationCalendarDay(start) === dayKey) {
        startMinute = minuteOfDay(start);
    } else {
        startMinute = start < dayStart ? 0 : MINUTES_PER_DAY;
    }

    let endMinute: number;
    if (stationCalendarDay(end) === dayKey) {
        endMinute = minuteOfDay(end);
    } else {
        endMinute = end < dayStart ? 0 : MINUTES_PER_DAY;
    }

    startMinute = clampToDay(startMinute);
    endMinute = clampToDay(Math.max(startMinute, endMinute));
    return { startMinute, endMinute };
};

/**
 * The stretches of `day` on which nothing occupies the object — the complement
 * of the merged occupying bookings.
 *
 * Only bookings that actually take the object off the market count (see
 * `occupiesAsset`), and a zero-length booking blocks nothing. An object with no
 * bookings comes back as one window covering the whole day.
 */
export const freeWindowsForDay = (
    orders: OrderForAssetAvailability[],
    day: Date,
): DaySpan[] => {
    const busy = orders
        .filter((order) => occupiesAsset(order.status))
        .map((order) => orderSpanWithinDay(order, day))
        .filter((span) => span.endMinute > span.startMinute)
        .sort((a, b) => a.startMinute - b.startMinute);

    const free: DaySpan[] = [];
    let cursor = 0;
    busy.forEach((span) => {
        if (span.startMinute > cursor) {
            free.push({ startMinute: cursor, endMinute: span.startMinute });
        }
        cursor = Math.max(cursor, span.endMinute);
    });
    if (cursor < MINUTES_PER_DAY) {
        free.push({ startMinute: cursor, endMinute: MINUTES_PER_DAY });
    }
    return free;
};

/** `540` → `09:00`; the end of the day reads `24:00`, not `00:00`. */
export const formatMinuteOfDay = (minute: number): string => {
    const hours = Math.floor(minute / 60);
    const minutes = minute % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/** `{ startMinute: 540, endMinute: 720 }` → `09:00 – 12:00`. */
export const formatDaySpan = (span: DaySpan): string =>
    `${formatMinuteOfDay(span.startMinute)} – ${formatMinuteOfDay(span.endMinute)}`;

/** Is the object free for the whole day — nothing booked at all? */
export const isFreeAllDay = (windows: DaySpan[]): boolean =>
    windows.length === 1 &&
    windows[0].startMinute === 0 &&
    windows[0].endMinute === MINUTES_PER_DAY;

/** Left offset / width of a span as a percentage of the day, for the timeline. */
export const spanToTrackStyle = (span: DaySpan): { left: string, width: string } => ({
    left: `${(span.startMinute / MINUTES_PER_DAY) * 100}%`,
    width: `${((span.endMinute - span.startMinute) / MINUTES_PER_DAY) * 100}%`,
});
